/* tslint:disable:no-eval */
import Polygon from 'polygon';
import { round } from 'lodash';
import { state } from '__common/store';
import {
  isRM10orRM10Evo,
  isRMGridFlex,
  isRmGridflex10D,
  isRmGridflex5Rwdi,
} from '__common/constants/products';
import { inchesToMeters } from '__common/calculations/inchesToMeters';
import { metersToFeets } from '__common/calculations/metersToFeets';
import { minDistanceToSegment } from '__common/calculations/distanceBetweenPointAndLineSegment';
import { lat_lng_to_pixels } from '__editor/panelsEditor/components/background/utils/backgroundCordinatesConverter';
import { greaterThanEqualToProjectVersion } from '__common/utils/versionCompare/versionCompare';
import { VERSION_MAP } from '__common/utils/versionCompare/version_info';

/**
 * The geometry the panels editor measures panel setbacks with, kept clear of the
 * rendering and UI modules that surround its callers so it can be reused - and
 * tested - on its own. tiltedRoof, background and roofEdgesCollisions re-export
 * from here, so callers do not need to know it moved.
 */

interface panelDimension {
  x: number;
  y: number;
  maxX: number;
  maxY: number;
}

export const ROOF_PITCH_GRIDFLEX: { [key: string]: string } = {
  ['---']: '0',
  ['0']: '0',
  ['1']: '1 / 57',
  ['2']: '3 / 86',
  ['3']: '1 / 19',
  ['4']: '3 / 43',
  ['5']: '7 / 80',
  ['6']: '2 / 19',
  ['7']: '7 / 57',
};

export const ROOF_PITCH_DEGREES: { [key: string]: string } = {
  ['---']: '0',
  ['0']: '0',
  ['1']: '1/57',
  ['2']: '3/86',
  ['3']: '1/19',
  ['4']: '3/43',
  ['5']: '1/12',
  ['6']: '2/19',
  ['7']: '7/57',
  ['8']: '9/64',
  ['9']: '2/12',
  ['10']: '3/17',
  ['11']: '7/36',
  ['12']: '17/80',
  ['13']: '3/13',
  ['14']: '3/12',
  ['15']: '26/97',
  ['16']: '27/94',
  ['17']: '26/85',
  ['18']: '4/12',
  ['19']: '21/61',
  ['20']: '4/11',
  ['21']: '38/99',
  ['22']: '40/99',
  ['23']: '5/12',
  ['24']: '41/92',
  ['25']: '7/15',
  ['26']: '20/41',
  ['27']: '6/12',
  ['28']: '42/79',
  ['29']: '51/92',
  ['30']: '7/12',
  ['31']: '3/5',
  ['32']: '5/8',
  ['33']: '50/77',
  ['34']: '8/12',
  ['35']: '7/10',
  ['36']: '69/95',
  ['37']: '9/12',
  ['38']: '25/32',
  ['39']: '17/21',
  ['40']: '10/12',
  ['41']: '73/84',
  ['42']: '9/10',
  ['43']: '11/12',
  ['44']: '28/29',
  ['45']: '12/12',
};

// adding toDetermineZoomLevel parameter to initialize zoom for panel editor else scaling only in y-direction
export const getScaleFromPitchRoof = (roofPitch: string, productId: number, toDetermineZoomLevel: boolean = false) => {
  let pitch;

  // roofpitch has not effect on scaling in y-direction for RM10, RM10EVO
  if (isRM10orRM10Evo(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) && !toDetermineZoomLevel) return 1;
  
  if (roofPitch === '---' && !isRMGridFlex(productId)) {
    pitch = eval('1/12');
  } else if (ROOF_PITCH_GRIDFLEX[roofPitch]) {
    pitch = eval(ROOF_PITCH_GRIDFLEX[roofPitch]);
  } else if (ROOF_PITCH_DEGREES[roofPitch]) {
    pitch = eval(ROOF_PITCH_DEGREES[roofPitch]);
  } else {
    pitch = eval(roofPitch);
  }

  const { settings: { panelHeight } } = state();
  const a = panelHeight * pitch;
  const ratio = Math.sqrt(a ** 2 + panelHeight ** 2);
  return ratio / panelHeight;
};

export const getRoofEdgesPointsInPx = (
  roofEdges: google.maps.LatLngLiteral[],
  roofCenter: cordPoint,
  zoom: number,
): pixelPoint[] => {
  const central_point = lat_lng_to_pixels(roofCenter, zoom);

  return roofEdges.reduce<pixelPoint[]>(
    (acc: pixelPoint[], cord: cordPoint) => {
      const pixels = lat_lng_to_pixels(
        { lat: cord.lat, lng: cord.lng },
        zoom,
        central_point,
        true,
        true,
      );
      const imageWidth = 1280;
      const imageHeight = 1280;
      const x = pixels.x - imageWidth;
      const y = pixels.y - imageHeight;
      acc.push({ x, y });
      return acc;
    },
    [],
  );
};

export const getRoofEdgesPxPoints = (roofEdges: google.maps.LatLngLiteral[], cords: cordPoint, zoom: number) => {
  cords = (!cords ? roofEdges[0] : cords);
  return getRoofEdgesPointsInPx(roofEdges, cords, zoom);
};

export const getRealRoofEdgesBoundsFromPixiCoords = (
  roofEdgesPxPoints: pixelPoint[],
  rotationRadians: number,
  roofPitch: string,
  shouldScale: boolean,
  productId: number,
  tilt: number) => {

  let notResized = new Polygon(roofEdgesPxPoints);
  if (shouldScale) notResized = notResized.scale(2);
  notResized = notResized.rotate(rotationRadians);

  if (!roofPitch) {
    return notResized;
  }
  
  const scale = getScaleFromPitchRoof(roofPitch, productId);

  const resizScaled = new Polygon(roofEdgesPxPoints)
    .scale(2)
    .scale(scale)
    .rotate(rotationRadians);

  const keepXCooridnate = (point, index) => {
    point.x = notResized.points[index].x;

    return point;
  };

  const rescaledYOnlyPoints = resizScaled
    .points
    .map(keepXCooridnate);

  resizScaled.points = rescaledYOnlyPoints;

  return resizScaled;
};

export const getRealRoofEdgesBounds = (
  input: {
    roofEdges: { lat: number, lng: number }[],
    roofCenter: { lat: number, lng: number},
    bgOffSet: {x: number, y: number,}
    zoom: number,
    rotationRadians: number,
    roofPitch: string,
    productId: number,
    tilt: number,
    },
    relativeToCenter = true,
    shouldScale = true,
): PolygonInterface => {
  let roofEdgesPxPoints: pixelPoint[];
  const {roofEdges, roofCenter, bgOffSet, zoom, rotationRadians, roofPitch, productId, tilt} =  input;

  if (relativeToCenter) {
    roofEdgesPxPoints = getRoofEdgesPxPoints(roofEdges, roofCenter, zoom).map(point => ({ x: point.x + bgOffSet.x, y: point.y + bgOffSet.y }));
  } else {
    roofEdgesPxPoints = getRoofEdgesPxPoints(roofEdges, roofCenter, zoom);
  }

  return getRealRoofEdgesBoundsFromPixiCoords(roofEdgesPxPoints, rotationRadians, roofPitch, shouldScale, productId, tilt);
};

function getSinglePanelPolygonDims(
  panel: panelInState,
  relativeToCenter: boolean,
  bgXOffset: number,
  bgYOffset: number,
  panelWidth: number,
  panelHeight: number,
): panelDimension {
  let x: number;
  let y: number;
  let maxX: number;
  let maxY: number;

  x = panel.x - (panelWidth / 2);
  y = panel.y - (panelHeight / 2);

  if (relativeToCenter) {
    x += bgXOffset;
    y += bgYOffset;
  }

  maxX = x + panelWidth;
  maxY = y + panelHeight;

  return {
    x,
    y,
    maxX,
    maxY,
  };
}

export function singlePanelPolygon(
  panel: panelInState,
  relativeToCenter: boolean,
  bgXOffset: number,
  bgYOffset: number,
  panelWidth: number,
  panelHeight: number,
  metersPerPixel: number,
  index?: number,
): PolygonInterface {
  const {projectConfiguration : {projectVersion, productId},} = state()
  const gf5SouthRailDistanceFromModuleEdge = 20 // 20 inches fixed for south ballast rail length from module edge in GF5.
  const gf5SouthRailLength = inchesToMeters(gf5SouthRailDistanceFromModuleEdge)

  const { x, y, maxX, maxY } = getSinglePanelPolygonDims(panel, relativeToCenter, bgXOffset, bgYOffset, panelWidth, panelHeight);

  return new Polygon([{
    x,
    y,
    index,
  },
  {
    x: maxX,
    y,
    index,
  },
  {
    x: maxX,
    y: (isRMGridFlex(productId) && greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['gf5_south_row_panel_editor_updates'])) ? maxY+(gf5SouthRailLength/metersPerPixel) : maxY,
    index,
  },
  {
    x,
    y: (isRMGridFlex(productId) && greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['gf5_south_row_panel_editor_updates'])) ? maxY+(gf5SouthRailLength/metersPerPixel) : maxY,
    index,
  },
  ]);
}

export const getSetBackDistanceBetweenPolygons = (
  panelPoly: PolygonInterface,
  roofPolygon: PolygonInterface,
  metersPerPixel: number,
): number => {
  const minDistToRoofEdges = Math.min(...panelPoly.points.map(point => Math.min(...roofPolygon.points.map((roofPoint, index) => minDistanceToSegment(point, roofPoint, roofPolygon.points[(index + 1) % roofPolygon.points.length])))))
  const minDistToPanelEdges = Math.min(...roofPolygon.points.map(point => Math.min(...panelPoly.points.map((panelPoint, index) => minDistanceToSegment(point, panelPoint, panelPoly.points[(index + 1) % panelPoly.points.length])))))
  const setBackDistanceInFt = metersToFeets(Math.min(minDistToPanelEdges, minDistToRoofEdges) * metersPerPixel);

  return round(setBackDistanceInFt, 2);
};